import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, Lightbulb, ThermometerSun, Clock, Sparkles, Eye } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_REMINDERS = [
  "Did you clock in?",
  "Are the A/C's on or off?",
  "That window is dirty, might want to clean it.",
  "Have you checked the lobby for trash?",
  "Don't forget to restock the prize drawer!",
  "Is the front door unlocked for customers?",
  "Did you put the sign out front?",
  "Have you tested all the room locks today?",
  "Is the music playing in the lobby?",
  "Check the restroom — does it need supplies?",
  "Have you wiped down the counter?",
  "Did anyone check the escape room reset?",
  "Is the booking tablet charged and on?",
  "Reminder: Smile — customers are watching!",
  "Are the lights set to the right mood?",
  "Has the trash been taken out today?",
  "Quick check — is the WiFi working for guests?",
  "Did you check today's reservations?",
  "Is the parking sign still visible outside?",
  "Water the plants if they look thirsty!",
  "Have you dusted the shelves recently?",
  "Double check the escape room timers are working.",
  "Is the first aid kit stocked?",
];

const ICONS = [Clock, ThermometerSun, Eye, Eye, Sparkles, AlertCircle, Lightbulb, AlertCircle, Sparkles, Eye, Eye, AlertCircle, Clock, Sparkles, Lightbulb, Eye, AlertCircle, Clock, Eye, Lightbulb, Eye, Clock, AlertCircle];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function AdminReminders() {
  const [messages, setMessages] = useState([]);
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/api/admin/reminders`, { withCredentials: true })
      .then(res => {
        const msgs = res.data.messages?.length ? res.data.messages : DEFAULT_REMINDERS;
        setMessages(msgs);
        setQueue(shuffle(msgs));
      })
      .catch(() => {
        setMessages(DEFAULT_REMINDERS);
        setQueue(shuffle(DEFAULT_REMINDERS));
      });
  }, []);

  const showNext = useCallback(() => {
    if (!queue.length) return;
    let q = queue;
    let i = idx;
    if (i >= q.length) {
      q = shuffle(messages);
      setQueue(q);
      i = 0;
    }
    setCurrent(q[i]);
    setIdx(i + 1);
  }, [queue, idx, messages]);

  const dismiss = () => setCurrent(null);

  useEffect(() => {
    if (!queue.length) return;
    const initial = setTimeout(() => showNext(), 20000);
    return () => clearTimeout(initial);
  }, [queue.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (current || !queue.length) return;
    const delay = 45000 + Math.random() * 45000;
    const timer = setTimeout(() => showNext(), delay);
    return () => clearTimeout(timer);
  }, [current, showNext, queue.length]);

  const Icon = ICONS[messages.indexOf(current)] || AlertCircle;

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          key={current}
          initial={{ opacity: 0, y: 50, x: -20 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: 30, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          className="fixed bottom-6 left-6 z-[9999] max-w-sm"
          data-testid="admin-reminder"
        >
          <div className="bg-card border border-border rounded-2xl shadow-xl p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-brand/15 flex items-center justify-center shrink-0 mt-0.5">
              <Icon size={18} className="text-brand" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-[10px] tracking-widest uppercase text-brand mb-1">Reminder</p>
              <p className="font-sans text-sm text-foreground leading-relaxed">{current}</p>
            </div>
            <button
              onClick={dismiss}
              data-testid="reminder-dismiss"
              className="text-muted-foreground hover:text-foreground transition-colors p-1 shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
